import { useCartDrawer } from '@iheartjane/cart';
import type { ReplacementCart } from '@iheartjane/cart/utils';
import {
  clearCheckoutError,
  getGroupSpecialSelection,
  getLocalCartUuid,
  getLocalDeliveryAddress,
  isSameStoreId,
  markCheckoutFailed,
  markCheckoutStarted,
  markCheckoutSucceeded,
  setGroupSpecialSelection,
  toDeliveryAddressParams,
  toReplacementCart,
  updateLocalCartUuid,
  updateLocalDeliveryAddress,
  useLocalTags,
} from '@iheartjane/cart/utils';
import type { Cart, CreateCartProps } from '@iheartjane/data-access';
import { ApiError, removeCartProduct } from '@iheartjane/data-access';
import {
  cartKeys,
  useAddCartProduct,
  useAddLoyaltyRedemption,
  useCheckout,
  useCreateCart,
  useDeleteCart,
  useRemoveCartProduct,
  useRemoveLoyaltyRedemption,
  useUpdateCart,
  useUpdateCartProduct,
} from '@iheartjane/data-access/react-query';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import { EventNames, trackError } from '@iheartjane/tracking';
import type { PriceId, ReservationMode } from '@iheartjane/types';
import { useQueryClient } from '@tanstack/react-query';
import noop from 'lodash-es/noop';
import pick from 'lodash-es/pick';
import { useCallback, useMemo } from 'react';
import { useStore } from 'react-redux';
import type { NavigateFunction } from 'react-router';

import { trackCartEvent } from '@jane/monolith/customer/cart';
import { runCheckoutSuccessEffects } from '@jane/monolith/customer/checkoutSuccessEffects';
import { useCustomerDispatch } from '@jane/monolith/customer/dispatch';
import type { AppState } from '@jane/monolith/customer/redux';
import { selectApplication } from '@jane/monolith/customer/redux/application';
import type {
  AttemptToAddToCartPayload,
  CartState,
} from '@jane/monolith/customer/redux/cart';
import {
  CRM_REDEMPTION_ERROR_MESSAGE,
  analyzeSpecials,
  applyCrmRedemption,
  attemptToAddToCart,
  checkout,
  deleteCartItem,
  deleteUnavailableProducts,
  maybeChangeStore,
  opensDrawerAfterAdd,
  refreshCart,
  removeCrmRedemption,
  removeDeliveryAddress,
  replaceCart,
  resetCart,
  selectCart,
  updateCart,
  updateDeliveryAddress,
  updateHeadlessDeliveryAddress,
  updateReservationMode,
} from '@jane/monolith/customer/redux/cart';
import { selectEmbeddedApp } from '@jane/monolith/customer/redux/embeddedApp';
import { selectStore } from '@jane/monolith/customer/redux/store';
import { useCustomerSelector } from '@jane/monolith/customer/selectors';
import { NotificationsService } from '@jane/monolith/services/notifications';
import type { CheckoutRequest } from '@jane/monolith/sources/cart';
import type { MoeEvent } from '@jane/shared-ecomm/tracking';
import { moeAddToBag, track } from '@jane/shared-ecomm/tracking';
import { postAnalyticsEvent } from '@jane/shared-ecomm/util';
import { getJwt } from '@jane/shared/auth';
import { checkoutErrorDetails } from '@jane/shared/data-access';
import { useEcommApp } from '@jane/shared/providers';
import type { CartLineItems } from '@jane/shared/types';
import { getActualUnitPrice, prepareCartProduct } from '@jane/shared/util';

import { EMPTY_V3_CART } from './emptyV3Cart';
import type { CheckoutPayload } from './toV3CheckoutProps';
import { toV3CheckoutProps } from './toV3CheckoutProps';

const REPLACE_CART_ERROR_MESSAGE =
  "We couldn't add that item to a new bag. Please try again.";

/** What a v3 delete needs; v1 and v3 `unavailable_products` rows both fit. */
export interface UnavailableProductRef {
  price_id: PriceId;
  product_id: number;
}

export interface CheckoutArgs {
  cartLineItems: CartLineItems;
  checkout: CheckoutPayload;
  isEmbeddedMode: boolean;
  isGuest: boolean;
  isStackAdaptPixelEnabled: boolean;
  moeTrack: (event: MoeEvent) => void;
  navigate: NavigateFunction;
  onSuccess: () => void;
}

/**
 * Cart writes: v3 mutations flag-on, v1 thunks flag-off. Actions without a v3
 * version yet use the v1 thunk either way.
 * TODO(carts-v3): move the remaining actions to v3.
 *
 * Flag-on, the v1-only actions still dispatch thunks, and the store-switch
 * guard opens the modal slice. The thunks' `bundlePossibilities` dispatches
 * are dropped on purpose (ADR-0003).
 */
export interface CartFacadeActions {
  /**
   * Flag-on, creates the cart if there isn't one. No `onAdded`: the facade
   * opens the drawer, so pass `withoutDrawer` instead.
   */
  addProduct: (payload: Omit<AttemptToAddToCartPayload, 'onAdded'>) => void;
  applyLoyalty: (...args: Parameters<typeof applyCrmRedemption>) => void;
  /** Flag-on, a failure only updates the status store; no toast or unavailable-products fix yet. */
  checkout: (args: CheckoutArgs) => void;
  deleteUnavailableProducts: (
    products: readonly UnavailableProductRef[]
  ) => void;
  /**
   * Re-prices the cart for the selected payment method. Flag-on a no-op: the
   * `paymentMethod`-keyed `useCartState` read re-prices on its own.
   */
  refreshCart: () => void;
  removeDeliveryAddress: () => void;
  removeLoyalty: (...args: Parameters<typeof removeCrmRedemption>) => void;
  removeProduct: (...args: Parameters<typeof deleteCartItem>) => void;
  /** Not an add, so only `moeAddToBag` fires. */
  replaceCart: (
    replacementCart: ReplacementCart,
    moeTrack: (event: MoeEvent) => void
  ) => Promise<void>;
  resetCart: (...args: Parameters<typeof resetCart>) => void;
  /** Flag-on this only saves the address; the new query key refetches. */
  updateDeliveryAddress: (
    ...args: Parameters<typeof updateDeliveryAddress>
  ) => void;
  /** `null` clears the pick. Flag-on, a failed PATCH restores the previous one. */
  updateGroupSpecial: (groupSpecialId: number | null) => Promise<void>;
  /** Flag-off, unlike `updateDeliveryAddress`, this skips the refetches. */
  updateHeadlessDeliveryAddress: (
    ...args: Parameters<typeof updateHeadlessDeliveryAddress>
  ) => void;
  updateProduct: (...args: Parameters<typeof updateCart>) => void;
  updateReservationMode: (
    ...args: Parameters<typeof updateReservationMode>
  ) => void;
}

// Stable default for trees that never loaded a cart.
const NO_PRODUCTS: CartState['cart']['products'] = [];

// The key `useCartState` observes; PATCHes send the same address so their
// responses land on it.
const activeAddress = () => toDeliveryAddressParams(getLocalDeliveryAddress());

export const useCartActions = (): CartFacadeActions => {
  const dispatch = useCustomerDispatch();
  const useV3Cart = useFlag(FLAGS.c3UseV3Cart);
  const { appMode } = useEcommApp();
  const { partnerId: storeId } = useCustomerSelector(selectEmbeddedApp);
  // The v3 cart's `store` is only `{ id }`; currency and name come from here.
  const { store } = useCustomerSelector(selectStore);
  const { close: closeCartDrawer, open: openCartDrawer } = useCartDrawer();
  // Flag-off uses these to close the drawer when a removal empties the cart.
  const v1CartProducts = useCustomerSelector(
    (state) => selectCart(state).cart?.products ?? NO_PRODUCTS
  );
  // Narrow selector: all of `selectCart` would re-render every consumer on any
  // cart change.
  const janeDeviceId = useCustomerSelector(
    (state) => selectApplication(state).janeDeviceId
  );
  const v1CartUuid = useCustomerSelector((state) =>
    useV3Cart ? undefined : selectCart(state).cart?.uuid
  );
  const v1PromoCode = useCustomerSelector((state) =>
    useV3Cart ? undefined : selectCart(state).cart?.promo_code
  );
  const v1ReservationMode = useCustomerSelector(
    (state): ReservationMode =>
      (!useV3Cart && selectCart(state).cart?.reservation_mode) || 'pickup'
  );
  const cartTags = useLocalTags(appMode, storeId);
  const queryClient = useQueryClient();
  // Read at success time, as v1 does: a guest's details land just before.
  const { getState } = useStore<AppState>();

  // `mutate` is stable, so the memo below doesn't rebuild every render.
  const { mutate: mutateUpdateCart, mutateAsync: mutateUpdateCartAsync } =
    useUpdateCart();
  const { mutate: mutateDeleteCart, mutateAsync: deleteCartAsync } =
    useDeleteCart();
  const { mutate: mutateUpdateCartProduct } = useUpdateCartProduct();
  const { mutate: mutateRemoveCartProduct } = useRemoveCartProduct();
  const { mutate: mutateAddLoyalty } = useAddLoyaltyRedemption();
  const { mutate: mutateRemoveLoyalty } = useRemoveLoyaltyRedemption();
  const { mutate: mutateAddCartProduct } = useAddCartProduct();
  const { mutateAsync: checkoutAsync } = useCheckout();
  // Save the new uuid here, once, rather than at each call site.
  const { mutate: mutateCreateCart, mutateAsync: createCartAsync } =
    useCreateCart({
      onSuccess: (cart) => updateLocalCartUuid(appMode, storeId, cart.uuid),
    });

  // Kept out of the memo below, which changes with every cart edit flag-off:
  // `CheckoutGroupDiscounts` re-applies the special whenever this changes.
  const updateGroupSpecial = useCallback<
    CartFacadeActions['updateGroupSpecial']
  >(
    async (groupSpecialId) => {
      const uuid = useV3Cart ? getLocalCartUuid(appMode, storeId) : v1CartUuid;
      if (!uuid) return;

      const previousSelection = getGroupSpecialSelection(uuid);
      setGroupSpecialSelection(uuid, groupSpecialId);

      try {
        if (useV3Cart) {
          await mutateUpdateCartAsync({
            appMode,
            deliveryAddress: activeAddress(),
            jwt: getJwt(),
            userGroupSpecialId: groupSpecialId,
            uuid,
          });
        } else {
          await dispatch(
            analyzeSpecials({
              promoCode: v1PromoCode ?? undefined,
              reservationMode: v1ReservationMode,
              groupSpecialId: groupSpecialId ?? undefined,
            })
          );
        }
      } catch (error) {
        setGroupSpecialSelection(uuid, previousSelection);
        trackError(error);
      }
    },
    [
      appMode,
      dispatch,
      mutateUpdateCartAsync,
      storeId,
      useV3Cart,
      v1CartUuid,
      v1PromoCode,
      v1ReservationMode,
    ]
  );

  return useMemo(() => {
    const openDrawerAfterAdd = (withoutDrawer?: boolean) => {
      if (opensDrawerAfterAdd(appMode, withoutDrawer)) openCartDrawer();
    };

    const v1Actions: CartFacadeActions = {
      addProduct: (payload) =>
        dispatch(
          attemptToAddToCart({
            ...payload,
            onAdded: () => openDrawerAfterAdd(payload.withoutDrawer),
          })
        ),
      updateProduct: (...args) => dispatch(updateCart(...args)),
      removeProduct: (productId, priceId) => {
        if (v1CartProducts.length === 1 && v1CartProducts[0].id === productId) {
          closeCartDrawer();
        }
        dispatch(deleteCartItem(productId, priceId));
      },
      updateReservationMode: (...args) =>
        dispatch(updateReservationMode(...args)),
      updateGroupSpecial,
      updateDeliveryAddress: (...args) =>
        dispatch(updateDeliveryAddress(...args)),
      removeDeliveryAddress: () => dispatch(removeDeliveryAddress()),
      updateHeadlessDeliveryAddress: (...args) =>
        dispatch(updateHeadlessDeliveryAddress(...args)),
      applyLoyalty: (...args) => dispatch(applyCrmRedemption(...args)),
      removeLoyalty: (...args) => dispatch(removeCrmRedemption(...args)),
      deleteUnavailableProducts: (products) => {
        if (!v1CartProducts.length) closeCartDrawer();
        dispatch(
          deleteUnavailableProducts(
            products.map((product) => product.product_id)
          )
        );
      },
      // TODO(carts-v3): delete this branch and its cast with the v1 checkout.
      checkout: ({ checkout: payload, ...args }) =>
        dispatch(
          checkout({ ...args, checkout: payload as unknown as CheckoutRequest })
        ),
      refreshCart: () => dispatch(refreshCart({ uuid: v1CartUuid ?? '' })),
      resetCart: (...args) => {
        closeCartDrawer();
        dispatch(resetCart(...args));
      },
      replaceCart: async (replacementCart, moeTrack) => {
        await dispatch(
          replaceCart(replacementCart, moeTrack, {
            close: closeCartDrawer,
            open: openCartDrawer,
          })
        );
      },
    };

    if (!useV3Cart) return v1Actions;

    const cartQueryKey = (uuid: string) =>
      cartKeys.detail(uuid, undefined, activeAddress());

    // Read from the cache, not a `useCart` observer, which would re-render
    // every consumer of this hook.
    const cartSnapshot = (uuid: string) =>
      queryClient.getQueryData<Cart>(cartQueryKey(uuid)) ?? null;

    // The checkout page reads the Jane Pay-priced cart under its own key.
    const checkoutCartSnapshot = (uuid: string, paymentMethod: string) =>
      (paymentMethod === 'jane_pay'
        ? queryClient.getQueryData<Cart>(
            cartKeys.detail(uuid, paymentMethod, activeAddress())
          )
        : undefined) ?? cartSnapshot(uuid);

    // Always refetch: the server may have destroyed the cart, and a 204 can't
    // tell us. A destroyed cart 404s, which `useCartState` handles.
    const settleAfterRemoval = (uuid: string) =>
      queryClient.invalidateQueries({ queryKey: cartKeys.detail(uuid) });

    // No `reservation_mode`: the server derives it.
    const createCartProps = (
      products: CreateCartProps['products'],
      newCartStoreId: number | string
    ): CreateCartProps => ({
      appMode,
      deviceId: janeDeviceId,
      jwt: getJwt(),
      products,
      storeId: newCartStoreId,
      tags: cartTags,
    });

    // Without a uuid, `/v3/carts//cart_products` 404s, so a first add creates
    // the cart with the product.
    const createV3Cart = (
      products: CreateCartProps['products'],
      newCartStoreId: number | string,
      onCreated?: (cart: Cart) => void
    ) =>
      mutateCreateCart(createCartProps(products, newCartStoreId), {
        onSuccess: onCreated,
      });

    const updateProduct: CartFacadeActions['updateProduct'] = (
      { count, price_id, product_id },
      moeTrack
    ) => {
      const uuid = getLocalCartUuid(appMode, storeId);
      const cart = uuid ? cartSnapshot(uuid) : null;
      if (!uuid || !cart) return;

      const cartProduct = cart.products.find(
        (product) =>
          product.id === product_id || product.product_id === product_id
      );
      const changeInCount = (count ?? 0) - (cartProduct?.count ?? 0);

      mutateUpdateCartProduct(
        {
          appMode,
          cartUuid: uuid,
          count,
          jwt: getJwt(),
          priceId: price_id,
          productId: Number(product_id),
        },
        {
          onSuccess: () => {
            if (changeInCount <= 0 || !cartProduct) return;
            moeTrack(
              moeAddToBag({
                cartUuid: uuid,
                product: { ...cartProduct, count: changeInCount },
                storeId: store?.id ?? null,
                storeName: store?.name ?? null,
              })
            );
          },
        }
      );
    };

    // Ignore blank addresses, as v1 did.
    const updateV3DeliveryAddress: CartFacadeActions['updateDeliveryAddress'] =
      ({ deliveryAddress }) => {
        if (!deliveryAddress.street?.length) return;
        updateLocalDeliveryAddress(deliveryAddress);
      };

    return {
      ...v1Actions,

      refreshCart: noop,

      updateProduct,

      // Same order as `attemptToAddToCart`: store guard, then analytics.
      addProduct: ({
        almostGone,
        columnPosition,
        count,
        location,
        menuProduct,
        moeTrack,
        navigate,
        price_id,
        productLocation,
        rowPosition,
        source,
        special,
        store: addStore,
        userPreferences,
        userSegments,
        withoutDrawer = false,
      }) => {
        const uuid = getLocalCartUuid(appMode, storeId);
        // No cart here means create, not a no-op.
        const cart = uuid ? cartSnapshot(uuid) : null;
        const storeAndProduct = prepareCartProduct(
          addStore,
          menuProduct,
          price_id,
          count
        );

        if (cart && !isSameStoreId(cart.store.id, addStore.id)) {
          return maybeChangeStore(
            appMode,
            dispatch,
            toReplacementCart({
              count,
              menuProduct,
              price_id,
              store: addStore,
            }),
            navigate
          );
        }

        // Matches v1's `isProductInCart` (on `id`, not `product_id`).
        const productInCart = cart?.products.some(
          (product) =>
            product.id === menuProduct.id && product.price_id === price_id
        );

        if (productInCart) {
          trackCartEvent({
            count,
            event: EventNames.UpdatedProductInCart,
            location,
            menuProduct,
            price_id,
            productLocation,
            source,
            special,
            store: addStore,
            userPreferences,
            userSegments,
          });
          updateProduct(
            { count, price_id, product_id: menuProduct.id },
            moeTrack
          );
          return;
        }

        const basePrice = Number(menuProduct[`price_${price_id}`]);
        const discountedPrice = Number(
          menuProduct[`discounted_price_${price_id}`]
        );
        postAnalyticsEvent({
          name: 'cartItemAdd',
          properties: {
            product: {
              price_id,
              ...pick(menuProduct, [
                'product_id',
                'name',
                'brand',
                'category',
                'kind',
              ]),
            },
            productId: menuProduct.product_id || menuProduct.id,
            quantity: count,
            ...(basePrice > 0 && { price: basePrice }),
            ...(discountedPrice > 0 && { discountedPrice }),
          },
        });

        trackCartEvent({
          almostGone,
          cartId: cart?.id,
          columnPosition,
          count,
          event: EventNames.AddedProductToCart,
          location,
          menuProduct,
          price_id,
          productLocation,
          rowPosition,
          source,
          special,
          store: addStore,
          userPreferences,
          userSegments,
        });

        const onAdded = (cartUuid: string) => {
          moeTrack(
            moeAddToBag({
              cartUuid,
              product: storeAndProduct.product,
              storeId: addStore.id,
              storeName: addStore.name,
            })
          );

          openDrawerAfterAdd(withoutDrawer);
        };

        // Send `menuProduct.id`: grid cards omit `product_id`, as v1 did.
        if (!uuid) {
          return createV3Cart(
            [{ count, price_id, product_id: menuProduct.id }],
            addStore.id,
            (created) => onAdded(created.uuid)
          );
        }

        mutateAddCartProduct(
          {
            appMode,
            cartUuid: uuid,
            deviceId: janeDeviceId,
            jwt: getJwt(),
            product: {
              count,
              price_id,
              product_id: menuProduct.id,
              // Never saved by the API; sent to match v1's payload.
              recommended: !!menuProduct.recommended,
            },
            storeId: addStore.id,
            tags: cartTags,
          },
          { onSuccess: (data) => onAdded(data.cart_uuid) }
        );
      },

      // Awaited, not per-call callbacks: those are skipped once the caller
      // unmounts. Create first so a failed create keeps the old bag.
      replaceCart: async ({ product, store: replacementStore }, moeTrack) => {
        closeCartDrawer();
        const oldUuid = getLocalCartUuid(appMode, storeId);

        try {
          const created = await createCartAsync(
            createCartProps(
              [
                {
                  count: product.count,
                  price_id: product.price_id,
                  product_id: product.product_id,
                },
              ],
              replacementStore.id
            )
          );

          if (oldUuid) {
            deleteCartAsync({ appMode, jwt: getJwt(), uuid: oldUuid }).catch(
              noop
            );
          }

          moeTrack(
            moeAddToBag({
              cartUuid: created.uuid,
              product,
              storeId: replacementStore.id,
              storeName: replacementStore.name,
            })
          );
          openDrawerAfterAdd();
        } catch (error) {
          NotificationsService.error(REPLACE_CART_ERROR_MESSAGE);
          trackError(error, { storeId: replacementStore.id });
        }
      },

      removeProduct: (productId, priceId) => {
        const uuid = getLocalCartUuid(appMode, storeId);
        const cart = uuid ? cartSnapshot(uuid) : null;
        if (!uuid || !cart) return;

        const product = cart.products.find(
          (candidate) =>
            candidate.id === productId && candidate.price_id === priceId
        );
        // v1's `cartEmptied` rule, not a guess at whether it's destroyed.
        const wasOnlyLine = cart.products.length === 1;

        if (wasOnlyLine && cart.products[0].id === productId) {
          closeCartDrawer();
        }

        if (product) {
          track({
            event: EventNames.RemovedProductFromCart,
            productBrand: product.brand,
            productCategory: product.category,
            productId: productId.toString(),
            productName: product.name,
            // Base price, no special, as v1 did.
            productPrice: getActualUnitPrice(product, product.price_id) || 0,
            quantity: product.count,
            storeCurrency: store?.currency_code,
          });
        }

        const basePrice = product ? Number(product[`price_${priceId}`]) : 0;
        const discountedPrice = product
          ? Number(product[`discounted_price_${priceId}`])
          : 0;

        mutateRemoveCartProduct(
          {
            appMode,
            cartUuid: uuid,
            jwt: getJwt(),
            priceId,
            productId: Number(productId),
          },
          {
            onSuccess: () => {
              postAnalyticsEvent({
                name: 'cartItemRemoval',
                properties: {
                  priceId,
                  productId,
                  ...(basePrice > 0 && { price: basePrice }),
                  ...(discountedPrice > 0 && { discountedPrice }),
                },
              });

              if (wasOnlyLine) postAnalyticsEvent({ name: 'cartEmptied' });

              settleAfterRemoval(uuid);
            },
          }
        );
      },

      updateReservationMode: ({ reservationMode }) => {
        const uuid = getLocalCartUuid(appMode, storeId);
        if (!uuid) return;

        const deliveryAddress = activeAddress();
        const queryKey = cartKeys.detail(uuid, undefined, deliveryAddress);
        const previousCart = queryClient.getQueryData<Cart>(queryKey);
        // Stop in-flight refetches from restoring the old mode. Sync; no await.
        queryClient.cancelQueries({ queryKey: cartKeys.detail(uuid) });
        // Optimistic: the toggle can't wait for the PATCH. Undone on error.
        if (previousCart) {
          queryClient.setQueryData<Cart>(queryKey, {
            ...previousCart,
            reservation_mode: reservationMode,
          });
        }

        mutateUpdateCart(
          {
            appMode,
            deliveryAddress,
            jwt: getJwt(),
            reservationMode,
            // The server drops an ineligible special; re-sending restores it.
            userGroupSpecialId: getGroupSpecialSelection(uuid) ?? undefined,
            uuid,
          },
          {
            onError: () => {
              // After a double toggle the snapshot is a guess too; refetch.
              if (previousCart) {
                queryClient.setQueryData(queryKey, previousCart);
              }
              queryClient.invalidateQueries({ queryKey });
            },
          }
        );
      },

      applyLoyalty: (crmParams) => {
        const uuid = getLocalCartUuid(appMode, storeId);
        if (!uuid) return;

        // Only the fields the v3 endpoint accepts.
        mutateAddLoyalty(
          {
            appMode,
            cartUuid: uuid,
            crmMemberPoints: crmParams.crm_member_points,
            crmProvider: crmParams.crm_provider,
            crmRewardId: String(crmParams.crm_reward_id),
            jwt: getJwt(),
            pointValue: crmParams.point_value,
            redemptionChecksum: crmParams.redemption_checksum,
            redemptionUrl: crmParams.redemption_url,
            reward: crmParams.reward,
            rewardName: crmParams.reward_name,
          },
          {
            onError: (error) => {
              const rejected =
                error instanceof ApiError && error.response.status === 422;

              NotificationsService.error(
                rejected ? error.message : CRM_REDEMPTION_ERROR_MESSAGE
              );
              trackError(error, {
                cartUuid: uuid,
                crmProvider: crmParams.crm_provider,
                crmRewardId: crmParams.crm_reward_id,
              });
            },
          }
        );
      },

      // v3 finds the redemption by id, so `crmProvider` is unused.
      removeLoyalty: (id) => {
        const uuid = getLocalCartUuid(appMode, storeId);
        if (!uuid) return;

        mutateRemoveLoyalty({
          appMode,
          cartUuid: uuid,
          jwt: getJwt(),
          redemptionId: String(id),
        });
      },

      deleteUnavailableProducts: (products) => {
        const uuid = getLocalCartUuid(appMode, storeId);
        if (!uuid || !products.length) return;

        // Close the drawer if no available products remain, as v1 did.
        if (!cartSnapshot(uuid)?.products.length) closeCartDrawer();

        // Delete the caller's rows; the cache may be stale here.
        const jwt = getJwt();

        // Raw API fn, not `useRemoveCartProduct`, to refetch once instead of
        // per delete. `allSettled` so one failure doesn't stop the rest.
        Promise.allSettled(
          products.map((product) =>
            removeCartProduct({
              appMode,
              cartUuid: uuid,
              jwt,
              priceId: product.price_id,
              productId: product.product_id,
            })
          )
        ).then((results) => {
          // v1 deleted in one request, so partial failure is new; log it.
          const rejected = results.filter(
            (result) => result.status === 'rejected'
          );
          if (rejected.length) {
            trackError(
              new Error('Failed to delete unavailable cart products'),
              {
                cartUuid: uuid,
                failed: rejected.length,
                reasons: rejected.map((result) => String(result.reason)),
                requested: products.length,
              }
            );
          } else {
            clearCheckoutError();
          }

          settleAfterRemoval(uuid);
        });
      },

      updateDeliveryAddress: updateV3DeliveryAddress,

      removeDeliveryAddress: () => updateLocalDeliveryAddress(null),

      // Identical flag-on; the two only differ flag-off.
      // TODO(carts-v3): merge into `updateDeliveryAddress` with the v1 path.
      updateHeadlessDeliveryAddress: updateV3DeliveryAddress,

      checkout: ({
        cartLineItems,
        checkout: payload,
        isEmbeddedMode,
        isGuest,
        isStackAdaptPixelEnabled,
        moeTrack,
        navigate,
        onSuccess,
      }) => {
        const { payment_method: paymentMethod, uuid } = payload;
        markCheckoutStarted();

        // Before `mutate`: `useCheckout` removes every entry under the uuid,
        // and clearing the uuid clears the stored address.
        const cart = checkoutCartSnapshot(uuid, paymentMethod) ?? {
          ...EMPTY_V3_CART,
          uuid,
        };
        const deliveryAddress = getLocalDeliveryAddress();

        // Awaited, not per-call callbacks, which are skipped after unmount.
        checkoutAsync(toV3CheckoutProps(payload, { appMode, jwt: getJwt() }))
          .then(
            (result) => {
              // First, as v1 does, so a throwing effect can't skip it.
              onSuccess();
              const { customer, store: storeState } = getState();
              runCheckoutSuccessEffects({
                appMode,
                cart,
                cartLineItems,
                customer,
                deliveryAddress,
                isEmbeddedMode,
                isGuest,
                isStackAdaptPixelEnabled,
                moeTrack,
                navigate,
                partnerId: storeId,
                paymentMethod,
                result,
                store: storeState.store,
              });
              updateLocalCartUuid(appMode, storeId, null);
              markCheckoutSucceeded();
            },
            (error) => {
              markCheckoutFailed({
                details: checkoutErrorDetails(error),
                // The server's message, which the banner and error hooks match on.
                error: error instanceof ApiError ? error.message : error,
              });
              queryClient.invalidateQueries({
                queryKey: cartKeys.detail(uuid),
              });
            }
          )
          // As v1 does when a success effect throws.
          .catch((error) =>
            markCheckoutFailed({ details: checkoutErrorDetails(error), error })
          );
      },

      resetCart: () => {
        closeCartDrawer();
        const uuid = getLocalCartUuid(appMode, storeId);
        if (!uuid) return;

        mutateDeleteCart(
          { appMode, jwt: getJwt(), uuid },
          { onSuccess: () => updateLocalCartUuid(appMode, storeId, null) }
        );
      },
    };
  }, [
    appMode,
    cartTags,
    checkoutAsync,
    closeCartDrawer,
    createCartAsync,
    deleteCartAsync,
    dispatch,
    getState,
    janeDeviceId,
    mutateAddCartProduct,
    mutateAddLoyalty,
    mutateCreateCart,
    mutateDeleteCart,
    mutateRemoveCartProduct,
    mutateRemoveLoyalty,
    mutateUpdateCart,
    mutateUpdateCartProduct,
    openCartDrawer,
    queryClient,
    store,
    storeId,
    updateGroupSpecial,
    useV3Cart,
    v1CartProducts,
    v1CartUuid,
  ]);
};
